/**
 * Mentions — the parse half of block **B17** (`BE-17.1`, `FR-COLLAB-007`),
 * extended by block **B30** (`BE-30.1`, `FR-COLLAB-008`) with the two reserved
 * group names.
 *
 * ⚠ **THIS FILE RESOLVES NOTHING.** `parseMentions` takes one argument — a
 * comment body — and returns the handles somebody typed. It does not know who
 * is speaking, which board the comment is on, whether any of those handles
 * names a real account, or whether that account may see the card. Every one of
 * those questions is answered by a **query** in
 * `apps/server/src/notifications/generate.ts`, inside the `WHERE` (SC-5), and
 * the split is the whole reason this is a pure function in `@molt/shared`
 * rather than a method on the generator: the grammar below has twenty-five
 * boundary cases and not one of them needs a container to assert.
 *
 * ⚠ **`parseGroupMentions` resolves nothing either**, and that is worth saying
 * twice because a group name *looks* like it has an obvious member set. It has
 * none here: `@card` becomes an `EXISTS` over `card_members` and `@board` an
 * `EXISTS` over `board_members`, both inside that same `WHERE`, both under the
 * same `isInBoardAudience` conjunct as an individual handle. This file only
 * says **which words were typed**.
 *
 * ---------------------------------------------------------------------------
 * WHICH LAYER DROPS WHAT — the table to read before "fixing" anything here
 * ---------------------------------------------------------------------------
 * | rule                                    | dropped by                      |
 * |-----------------------------------------|---------------------------------|
 * | `@` inside code                         | **this file**, passes 1–2       |
 * | the same handle twice                   | **this file**, pass 4           |
 * | `@board`/`@card` never matching an account | **this file** — excluded from `parseMentions` (B30/D1) |
 * | mentioning yourself                     | the resolution query — `u.id IS DISTINCT FROM $actorUserId` |
 * | a user who cannot see the board         | the resolution query — `isInBoardAudience($boardId)` |
 * | a **group** member who cannot see the board | the resolution query — the *same* `isInBoardAudience($boardId)`, deliberately not a per-arm exception |
 * | a deleted account                       | the resolution query — `u.deleted_at IS NULL` |
 * | `@nobody`, matching no account          | the resolution query, by returning no row |
 *
 * The lower rows are the ones a reader is most likely to want to move up here,
 * and all of them are impossible: this function has no actor, no board and no
 * membership table. A self-mention looks identical to any other mention from
 * inside the string, and `@card` looks identical whether the card has three
 * members or none.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `@board` AND `@card` ARE **RESERVED GROUP NAMES** — B30, `FR-COLLAB-008`
 * ---------------------------------------------------------------------------
 * They are **always** groups and **never** accounts. `parseMentions` excludes
 * them from its result; `parseGroupMentions` returns them and nothing else.
 * Both project the same scan, so the two can never disagree about what a
 * mention is (see `scanMentionCandidates`).
 *
 * ⚠ **The cost, stated rather than hidden: an account legally holding the
 * username `board` or `card` becomes unreachable by `@`-handle.**
 * `apps/server/src/auth/profile.ts` reserves no usernames — there is no
 * reserved list to grep — so the collision is creatable today. This goes the
 * same way, and for the same reason, as the trailing-dot trade in pass 3 below:
 * the failure that matters is the one where a **group** mention silently
 * resolves to one surprised individual instead of forty people, and *every*
 * `@board` in the product would hit it. The group meaning is the documented
 * product behaviour (`FR-COLLAB-008`); the colliding username is an accident of
 * sign-up order.
 *
 * **Rejected: resolve `@board` to the account when one exists and to the group
 * otherwise.** That makes the meaning of a comment depend on the account table
 * at post time — the same text notifies a stranger on Monday and the whole
 * board on Tuesday. A mention whose semantics are data-dependent is not a
 * feature.
 *
 * **The durable fix is a reserved-username list in `profile.ts`** so the
 * collision cannot be created at all. That belongs to account lifecycle, not to
 * B30, and it is named here so it has an owner rather than being rediscovered.
 *
 * ⚠ **Two exports, one scan — not one restructured return.** `parseMentions`
 * keeps its exact signature and arity so that
 * `apps/server/src/notifications/generate.ts`'s pinned line
 * `const handles = parseMentions(row.body);` is untouched by B30, along with
 * every source-text assertion around it in
 * `apps/server/test/unit/FR-COLLAB-007.spec.ts`. Returning
 * `{ handles, groups }` reads tidier and reddens pins that have nothing to do
 * with group mentions, which invites whoever fixes them to weaken the ones that
 * matter. A sentinel inside the handle list (`'@board'`, an object) is worse
 * still: it makes every downstream reader special-case a string. Two exports
 * make the type honest and the diff legible as an addition.
 *
 * ---------------------------------------------------------------------------
 * NO `MENTION_CANDIDATES_MAX`, AND THAT IS A DECISION
 * ---------------------------------------------------------------------------
 * A cap would have to **drop** candidates past it, and a silently dropped
 * mention is the single worst thing this block can ship: the author sees their
 * `@alice` rendered, alice is never told, and no status code, no schema and no
 * test says so. The bound already exists one layer up —
 * `COMMENT_BODY_MAX_LENGTH` is 16,384, so a body cannot hold more than about
 * eight thousand candidates — and the resolution query is
 * `username = ANY($1)` against `uq_users_username`, one index scan whatever the
 * array length.
 */

/**
 * The two reserved group names — `FR-COLLAB-008`, block **B30**.
 *
 * ⚠ **EVERYTHING IN THIS FILE IS DERIVED FROM THIS ARRAY.** The literals
 * `'board'` and `'card'` appear here and nowhere else in the module, so adding
 * a third group name is one edit and cannot leave one of the two functions
 * behind. `as const` is load-bearing: it is what makes `GroupMention` a union of
 * the two names rather than `string`.
 *
 * The population each one resolves to lives in the query, not here:
 * `@board` → `board_members` (**every** row, observers included —
 * `FR-COLLAB-008` qualifies its "every member of the board" by no role, and
 * whether an observer may *reply* is `FR-COLLAB-015`/B59's question);
 * `@card` → `card_members`, which has no `role` column at all.
 */
export const GROUP_MENTION_NAMES = ['board', 'card'] as const;

/** One of the reserved group names. Derived — never written out by hand. */
export type GroupMention = (typeof GROUP_MENTION_NAMES)[number];

/**
 * `GROUP_MENTION_NAMES` as a set, for the membership test the two exports share.
 *
 * Typed `ReadonlySet<string>` rather than `ReadonlySet<GroupMention>` on
 * purpose: the scan produces `string`s, and a `Set<GroupMention>.has()` would
 * refuse them at compile time and tempt somebody into an `as` cast. The
 * narrowing is done properly by `isGroupMention` below.
 */
const GROUP_MENTION_NAME_SET: ReadonlySet<string> = new Set<string>(GROUP_MENTION_NAMES);

/**
 * The scanned candidate is one of the reserved group names.
 *
 * ⚠ A **type predicate**, not a cast. `GROUP_MENTION_NAMES.includes(candidate)`
 * does not narrow `string` to `GroupMention` against a readonly tuple, and the
 * two ways out of that are this one and `as GroupMention`. The cast would keep
 * compiling if the set and the union ever drifted apart; this signature cannot.
 */
function isGroupMention(candidate: string): candidate is GroupMention {
  return GROUP_MENTION_NAME_SET.has(candidate);
}

/**
 * Above this many recipients `FE-30.2`'s composer shows a confirmation naming
 * how many people will be notified — `FR-COLLAB-008`'s first edge case, which
 * suggests 25 and calls the number a product decision.
 *
 * ⚠⚠ **THE SERVER DOES NOT ENFORCE THIS AND NEVER REFUSES A LARGE `@board`.**
 * The edge case says *show a confirmation*, which is a client affordance; a
 * server that refused would contradict AC2 of its own requirement ("a board has
 * forty members … every board member except me is notified once"). It lives
 * here, next to `GROUP_MENTION_NAMES`, so the composer, the documentation and
 * `.b30-design.md` agree on one number instead of three.
 *
 * ⚠ **Its only consumer is `FE-30.2`, which is not built yet**, so a later
 * cleanup will read it as dead code. `mention.test.ts` pins the value for
 * exactly that reason: deleting it must redden something.
 */
export const GROUP_MENTION_CONFIRM_THRESHOLD = 25;

/**
 * The characters a username may contain.
 *
 * `apps/server/src/auth/profile.ts`'s `USERNAME_ALLOWED` (`/[^a-z0-9._-]+/g`)
 * **inverted**, and widened to include upper case for one reason: that regex
 * describes what the server *mints*, and this one describes what a human
 * *types*. `@Alice` is a person meaning `alice`, and pass 3 folds it (see
 * below); refusing the capital here would make the commonest typo in the
 * product unmentionable.
 *
 * ⚠ **No `g` flag, deliberately.** A `g`-flagged regex carries `lastIndex`
 * across `.test()` calls, so a module-level instance would skip the start of
 * the second body it ever saw — a mention dropped on every request after the
 * first, silently, with the first request passing every test. It is exported
 * so `FE-17.1`'s composer can highlight the same run this parser will take,
 * and a client that reused a `g`-flagged copy would get the same bug in the
 * browser.
 */
export const MENTION_NAME_CHARS = /[A-Za-z0-9._-]/;

/**
 * The characters that may sit immediately before a `@` for it to open a
 * mention. Start-of-input counts too, and is handled separately.
 *
 * ⚠ **An ALLOWLIST, not a denylist**, and that single choice is what kills
 * `bob@alice.com` and `https://x.test/@alice` without one line of e-mail or URL
 * logic anywhere. A denylist would have to enumerate every character that can
 * precede an address, would miss one, and would notify a user because somebody
 * quoted a mailto: link.
 *
 * ⚠ **THE BACKTICK IS IN THIS SET, AND IT IS NOT AN OVERSIGHT.** By the time
 * pass 3 runs, every backtick that opened a *closed* inline span has been
 * blanked out by pass 2. A backtick that survives is an **unmatched** run,
 * which CommonMark makes literal text — so `` `@alice `` (no closing tick) must
 * mention, and that is the asymmetry with pass 1 this file exists to get right.
 * Omit the backtick here and the parser answers `[]` for it while still
 * answering `["alice"]` for `@alice`, which reads as a bug in the fence logic
 * and is not.
 */
const MENTION_PRECEDERS = new Set([
  ' ',
  '\t',
  '\n',
  '\r',
  '\f',
  '\v',
  '(',
  '[',
  '{',
  '<',
  '"',
  "'",
  '`',
]);

/** Trailing punctuation trimmed off a candidate run — see pass 3, step 3. */
const MENTION_TRAILING_TRIM = new Set(['.', '-', '_']);

/**
 * Blank a region of the body, preserving its length and its newlines.
 *
 * ⚠ **THE PASSES BLANK, THEY NEVER SPLICE, AND SPLICING IS THE SILENT BUG.**
 * Removing a code span by cutting it out of the string makes neighbours of
 * characters that were never adjacent: `` a`x`@alice `` spliced becomes
 * `a@alice`, whose `@` is now preceded by a letter, so the mention **vanishes**
 * — a 200, a comment that renders the handle, and nobody notified. Blanking to
 * spaces leaves `a   @alice`, where the `@` is preceded by whitespace and the
 * mention survives. Newlines are preserved because pass 1 is line-based and
 * collapsing them would merge a fence into its neighbours.
 *
 * No table row in `mention.test.ts` reaches this by accident, which is exactly
 * why that file asserts it on purpose.
 */
function blank(body: string, start: number, end: number): string {
  let out = '';
  for (let i = start; i < end; i += 1) {
    out += body[i] === '\n' ? '\n' : ' ';
  }
  return body.slice(0, start) + out + body.slice(end);
}

/** The length of the run of `character` starting at `index`, possibly zero. */
function runLength(body: string, index: number, character: string): number {
  let length = 0;
  while (index + length < body.length && body[index + length] === character) length += 1;
  return length;
}

/**
 * **Pass 1 — fenced code blocks.** Line-based, CommonMark's rule.
 *
 * A line **opens** a fence when, after at most three leading spaces, it begins
 * with a run of **N ≥ 3** backticks. It **closes** at the first later line
 * that, after at most three leading spaces, begins with a run of **≥ N**
 * backticks followed by nothing but whitespace. ⚠ **An unterminated fence runs
 * to the end of the input** — that is CommonMark and it is also the safe
 * direction: a body that opens a fence and never closes it renders as code to
 * the end, so a `@handle` after it is code to the reader too, and notifying on
 * it would tell somebody they were mentioned in text nobody can see them in.
 *
 * ⚠ **BACKTICK FENCES ONLY. TILDE (`~~~`) FENCES ARE A KNOWN, NAMED GAP.**
 * CommonMark admits them; nothing in this product emits them (B16's composer
 * and `FE-17.1` insert backticks) and the failure is asymmetric — an
 * unterminated `~~~` under a symmetric implementation would swallow every
 * mention after it, which is the direction that loses notifications rather than
 * the direction that adds one. Owner: whoever ships the Markdown renderer
 * (`FR-COLLAB-004`, V1). `mention.test.ts` case 24 asserts the gap
 * **deliberately**, so it is a recorded behaviour rather than a discovery.
 */
function blankFencedCodeBlocks(body: string): string {
  let out = body;
  let lineStart = 0;
  let fence = 0; // 0 = not inside one; otherwise the opening run's length.

  while (lineStart <= out.length) {
    const newline = out.indexOf('\n', lineStart);
    const lineEnd = newline === -1 ? out.length : newline;
    const line = out.slice(lineStart, lineEnd);

    // At most three leading spaces, per CommonMark. A fourth makes the line an
    // indented code block, which this parser does not model — and which
    // FR-COLLAB-004's supported set does not list, so nothing renders it as
    // code either.
    let indent = 0;
    while (indent < 3 && line[indent] === ' ') indent += 1;
    const ticks = runLength(line, indent, '`');

    if (fence === 0) {
      if (ticks >= 3) {
        fence = ticks;
        out = blank(out, lineStart, lineEnd);
      }
    } else {
      const closes = ticks >= fence && line.slice(indent + ticks).trim() === '';
      out = blank(out, lineStart, lineEnd);
      if (closes) fence = 0;
    }

    if (newline === -1) break;
    lineStart = newline + 1;
  }

  return out;
}

/**
 * **Pass 2 — inline code spans**, over what pass 1 left.
 *
 * A run of **exactly N** backticks opens a candidate span, closed by the next
 * run of **exactly N** backticks. If no such run exists the opening run is
 * **literal text and opens nothing**, and scanning continues at the run after
 * it — again CommonMark.
 *
 * ⚠ **THIS IS THE ASYMMETRY IMPLEMENTATIONS GET BACKWARDS, AND IT IS WHY THE
 * TWO PASSES ARE SEPARATE FUNCTIONS.** An unterminated *fence* swallows the
 * rest of the document; an unterminated *inline* run swallows **nothing**, so
 * `` `@alice `` does mention. Making the two consistent in either direction is
 * wrong: symmetric-swallowing loses every mention after a stray backtick, and
 * symmetric-literal makes `` `@alice` `` notify from inside code.
 *
 * "Exactly N" rather than "at least N" is what makes case 17 work — inside a
 * ``` `` ``` span a single ``` ` ``` is content, not a closer.
 */
function blankInlineCodeSpans(body: string): string {
  // Every maximal backtick run, left to right. Collected up front because the
  // closer search has to look *past* runs that do not match the opener's
  // length, and blanking as we go would destroy the ones we still need to see.
  const runs: { start: number; length: number }[] = [];
  for (let i = 0; i < body.length;) {
    if (body[i] === '`') {
      const length = runLength(body, i, '`');
      runs.push({ start: i, length });
      i += length;
    } else {
      i += 1;
    }
  }

  let out = body;
  for (let opener = 0; opener < runs.length; opener += 1) {
    const open = runs[opener]!;
    const closer = runs.findIndex(
      (run, index) => index > opener && run.length === open.length,
    );
    if (closer === -1) continue; // Literal — CommonMark. Try the next run.
    const close = runs[closer]!;
    out = blank(out, open.start, close.start + close.length);
    opener = closer; // Everything between is consumed; resume after the closer.
  }

  return out;
}

/**
 * **THE ONE SCAN.** Every `@`-run in a comment body — individual handles *and*
 * the reserved group names, undivided — **ordered by first appearance, deduped,
 * lower-cased**. Resolves nothing.
 *
 * ⚠⚠ **`parseMentions` and `parseGroupMentions` are two projections of this one
 * list, and that is the point.** A second scan would be a second grammar: two
 * definitions of which `@` opens a mention, drifting the first time somebody
 * fixes a fence bug in one of them. Anything that changes what counts as a
 * mention changes it here, once, for both.
 *
 * ⚠ **The dedupe (pass 4) happens HERE, before the split.** `@card and @card`
 * must be one group, and splitting a list that still held duplicates would let
 * the split reintroduce what pass 4 removed.
 *
 * ---------------------------------------------------------------------------
 * PASS 3 — the mention scan, over what passes 1 and 2 left
 * ---------------------------------------------------------------------------
 *   1. **The preceding character** must be start-of-input or a member of
 *      `MENTION_PRECEDERS`. A letter, a digit, a `.`, a `/` or a `@` before the
 *      `@` makes it literal.
 *   2. **The run** is the maximal run of `MENTION_NAME_CHARS` immediately after
 *      the `@`. An empty run yields nothing and the scanner resumes at the next
 *      character, so `@@alice` and a bare `@` both produce nothing.
 *   3. **Trailing `.`, `-` and `_` are trimmed.** ⚠ The trade, stated rather
 *      than hidden: `sanitiseUsername` can in principle mint a username ending
 *      in a dot, and such an account becomes unmentionable — while every
 *      `@alice.` at the end of a sentence resolves. Sentences vastly outnumber
 *      those usernames, and the failure that matters is the one where a real
 *      mention is lost. A run that trims away to nothing yields nothing.
 *   4. **Lower-cased.** ⚠ **NOT `lower(username) = lower($1)` at the database,
 *      which is the tempting spelling and is WRONG here.** `uq_users_username`
 *      is case-**sensitive** under Postgres — one of the port's recorded
 *      "expected differences from MySQL", whose `utf8mb4_0900_ai_ci` folded it
 *      — so `alice` and `Alice` are two legal rows and a folded comparison can
 *      match **both**, notifying the wrong person or two people. Folding the
 *      *candidate* and comparing with exact equality can never match two rows,
 *      uses the unique index, and still handles the capitalised typo.
 *      ⚠ It depends on `apps/server/src/auth/profile.ts` lower-casing every
 *      username it mints, and that is currently the **only** writer. A future
 *      rename route admitting an upper-case character would silently make those
 *      accounts unmentionable — it fails closed, but it fails silently, so it
 *      is named here.
 *   5. Scanning resumes **after** the run, so an `@` inside a run cannot open a
 *      second candidate.
 *
 * **Pass 4 — dedupe**, preserving first appearance. `FR-COLLAB-007` AC3: the
 * same member mentioned twice in one comment is notified **once**. Doing it
 * here rather than in SQL means the resolution query's `ANY($1)` array is
 * already minimal and the notification insert cannot depend on a `DISTINCT`
 * somebody might drop.
 */
function scanMentionCandidates(body: string): string[] {
  const scannable = blankInlineCodeSpans(blankFencedCodeBlocks(body));

  const found: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < scannable.length; i += 1) {
    if (scannable[i] !== '@') continue;

    const preceding = i === 0 ? null : scannable[i - 1]!;
    if (preceding !== null && !MENTION_PRECEDERS.has(preceding)) continue;

    let end = i + 1;
    while (end < scannable.length && MENTION_NAME_CHARS.test(scannable[end]!)) end += 1;

    // Empty run: nothing to resolve, and the scanner deliberately resumes at
    // i + 1 rather than at `end` so that the character after a bare `@` gets
    // its own turn.
    if (end === i + 1) continue;

    let candidate = scannable.slice(i + 1, end);
    while (
      candidate.length > 0 &&
      MENTION_TRAILING_TRIM.has(candidate[candidate.length - 1]!)
    ) {
      candidate = candidate.slice(0, -1);
    }

    // Resume after the whole run even when the trim emptied it — the run was
    // consumed as a candidate either way, and re-scanning inside it could only
    // find an `@` preceded by a name character, which rule 1 refuses anyway.
    i = end - 1;

    if (candidate.length === 0) continue;

    const handle = candidate.toLowerCase();
    if (seen.has(handle)) continue;
    seen.add(handle);
    found.push(handle);
  }

  return found;
}

/**
 * Candidate **individual** handles in a comment body: ordered by first
 * appearance, deduped, lower-cased, with the reserved group names removed.
 * Resolves nothing.
 *
 * ⚠ **Arity 1, and it must stay 1.** `FR-COLLAB-007.spec.ts` asserts
 * `expect(parseMentions).toHaveLength(1)` as the only way a pure test can show
 * that the actor is not available to this layer — the self-drop is the
 * resolution query's, per the table in the header.
 *
 * ⚠ **`@board` and `@card` are gone from this result and are NOT lost** — they
 * come back from `parseGroupMentions` over the same scan. Before B30 they were
 * returned here as ordinary candidates, which resolved only if somebody
 * literally held the username; see the reserved-names section of the header for
 * why that reading is now wrong.
 */
export function parseMentions(body: string): string[] {
  return scanMentionCandidates(body).filter((candidate) => !isGroupMention(candidate));
}

/**
 * The reserved **group** names a comment body addressed — `@board`, `@card` —
 * ordered by first appearance and deduped. Resolves nothing: which people
 * `@board` means is a `board_members` `EXISTS` arm in
 * `apps/server/src/notifications/generate.ts`, under the same permission
 * conjuncts as an individual handle.
 *
 * The whole grammar applies, because this is the same scan `parseMentions`
 * projects: a fenced or inline-code `@board` is invisible, the preceder
 * allowlist means `x@board` and `https://x.test/@board` are not mentions,
 * `@Board` folds to `board`, `@board.` trims to `board`, and `@board @board` is
 * one entry.
 *
 * ⚠ **An empty result is the overwhelmingly common case** and is not the same
 * thing as an empty `parseMentions` — the generator's short-circuit must
 * require *both* to be empty, or a comment whose entire content is `@board`
 * silently notifies nobody.
 */
export function parseGroupMentions(body: string): GroupMention[] {
  return scanMentionCandidates(body).filter(isGroupMention);
}
